import { Cascader, Skeleton } from 'antd';
import { useEffect, useState } from 'react';
import { GrispiInputProps } from '..';
import useCascaderAssigneeList, {
    AssigneeCascaderOptionType
} from '../../../hooks/ticketDetailPage/useCascaderAssigneeList';
import GrispiInputContainer from '../GrispiInputContainer';

type GrispiAssigneeInputProps = Omit<GrispiInputProps<'ASSIGNEE'>, 'type' | 'options' | 'isNewValueAllowed'>;

const transformAssigneeArrayValue = (value: (number | ':currentUser' | ':requester' | ':notAssigned')[]) => {
    if (value?.includes(':currentUser')) return ':currentUser';
    if (value?.includes(':requester')) return ':requester';
    if (value?.includes(':notAssigned')) return ':notAssigned';
    if (value?.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number') return value.join(':');
    if (value?.length === 1 && typeof value[0] === 'number') return `${value.at(0)}:null`;
    return null;
};

const transformFormValue = (value: string) => {
    if (value && typeof value === 'string') {
        if (value === ':currentUser') return [':currentUser'];
        if (value === ':requester') return [':requester'];
        if (value === ':notAssigned') return [':notAssigned'];
        const [groupId, assigneeId] = value.split(':');
        if (groupId && !isNaN(+groupId) && assigneeId && !isNaN(+assigneeId)) return [+groupId, +assigneeId];
        if (groupId && !isNaN(+groupId)) return [+groupId];
    }
};

function filterCascaderSearch(inputValue: string, options: AssigneeCascaderOptionType[]) {
    return options.some(
        (option) => option.label.toLocaleLowerCase('tr').indexOf(inputValue.toLocaleLowerCase('tr')) > -1
    );
}

const GrispiAssigneeInput = ({
    antdProps,
    conditionInput = false,
    actionInput = false,
    containerProps,
    onChange,
    value,
    withEmptyOption = false,
    ...rest
}: GrispiAssigneeInputProps) => {
    const { options, optionsWithEmptyOption, optionsForAction, optionsForCondition } = useCascaderAssigneeList();
    const [assigneeElementId, setAssigneeElementId] = useState(null);

    useEffect(() => {
        const id = Math.floor(Math.random() * 10000);
        setAssigneeElementId(`antd-assignee-input-${id}`);
    }, []);

    useEffect(() => {
        if (!assigneeElementId) {
            return;
        }

        const sidebar = document.querySelector(
            'div.tab-content-container section.ant-layout.ant-layout-has-sider > aside.ant-layout-sider'
        );

        const onScroll = () => {
            const input: HTMLElement = document.getElementById(assigneeElementId);
            if (!input) return;

            const cascader: HTMLElement = input.closest('.custom-field-item')?.querySelector('.antd-cascader-fixed');
            const rect: DOMRect = input.getBoundingClientRect();

            cascader?.style?.setProperty('top', 3 + rect.y + rect.height + 'px');
        };

        sidebar?.addEventListener('scroll', onScroll);
        document.addEventListener('scroll', onScroll);

        return () => {
            sidebar?.removeEventListener('scroll', onScroll);
            document.removeEventListener('scroll', onScroll);
        };
    }, [assigneeElementId]);

    return (
        <GrispiInputContainer {...containerProps}>
            {options.length ? (
                <Cascader<AssigneeCascaderOptionType>
                    {...antdProps}
                    id={assigneeElementId}
                    multiple={false}
                    changeOnSelect
                    onChange={(v: (number | ':currentUser' | ':requester' | ':notAssigned')[]) =>
                        onChange(transformAssigneeArrayValue(v))
                    }
                    options={
                        actionInput
                            ? optionsForAction
                            : conditionInput
                            ? optionsForCondition
                            : withEmptyOption
                            ? optionsWithEmptyOption
                            : options
                    }
                    value={transformFormValue(value)}
                    popupClassName="antd-cascader-fixed"
                    showSearch={{ filter: filterCascaderSearch as any }}
                    placement="bottomRight"
                    builtinPlacements={{
                        bottomRight: {
                            points: ['tl', 'bl'],
                            offset: [0, 0],
                            dynamicInset: false,
                            targetOffset: [0, 0]
                        }
                    }}
                    {...rest}
                />
            ) : (
                <Skeleton.Input className="full-width" active />
            )}
        </GrispiInputContainer>
    );
};

export default GrispiAssigneeInput;
